import { Checkbox, Flex, type MantineSize } from "@mantine/core";
import type { ChangeEventHandler, ReactNode } from "react";

export interface IconCheckboxOption {
  value: string;
  icon: React.ComponentType<
    { size?: number | string } & Record<string, unknown>
  >;
}

interface IconCheckboxGroupProps {
  value: string;
  onChange: (value: string) => void;
  options: IconCheckboxOption[];
  size?: MantineSize;
  iconSize?: number;
  error?: ReactNode;
  allowDeselect?: boolean;
  gap?: string | number;
}

const getIconCheckboxStyles = (isChecked: boolean) => ({
  labelWrapper: { display: "none" },
  inner: { cursor: "pointer" },
  input: {
    cursor: "pointer",
    transition: "none",
  },
  icon: {
    opacity: 1,
    transform: "none",
    transition: "none",
    color: isChecked
      ? "var(--checkbox-icon-color)"
      : "var(--mantine-text-color)",
  },
});

const IconCheckboxGroup = ({
  value,
  onChange,
  options,
  size = "xl",
  iconSize = 16,
  error,
  allowDeselect = true,
  gap = "sm",
}: IconCheckboxGroupProps) => {
  const handleCheck: ChangeEventHandler<HTMLInputElement> = e => {
    if (e.target.checked) {
      onChange(e.target.value);
      return;
    }

    if (allowDeselect) {
      onChange("");
    }
  };

  return (
    <Flex align="center" gap={gap}>
      {options.map(option => {
        const isChecked = value === option.value;
        const Icon = option.icon;

        return (
          <Checkbox
            key={option.value}
            checked={isChecked}
            value={option.value}
            error={error}
            onChange={handleCheck}
            icon={iconProps => <Icon {...iconProps} size={iconSize} />}
            size={size}
            styles={getIconCheckboxStyles(isChecked)}
          />
        );
      })}
    </Flex>
  );
};

export default IconCheckboxGroup;
